import { useLocation, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Input,
  List,
  Tooltip,
  Typography,
  Skeleton,
  notification,
} from 'antd';
import { RedoOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react-lite';
import { Dynamics } from '@/services/dynamics/dynamics';
import { IEntity } from '@/shared/interfaces/entity.interface';
import { EntityItem } from '@/components/EntityItem/EntityItem';
import { EEntity } from '@/services/dynamics/enums/entity.enum';
import { ICrmMarketingList } from '@/shared/interfaces/crm-marketing-list.interface';
import { State } from '@/services/state.service';
import { useStore } from '@/stores';
import { commonMakeCall } from '@/shared/helpers/common-make-call.helper';
import { SipJs } from '@/services/sip-js.service';
import { CALL_PAGE_TYPE } from '@/shared/constants/callPage';
import './Dialer.scss';

const { Search } = Input;
const { Title, Text } = Typography;

export const Dialer = observer(() => {
  const {
    dialerStore: { setActiveDialerList, toggleDialler, isDialerActive },
    callStore: { callPage },
  } = useStore();

  const { page } = callPage;

  const params = useParams();
  const location = useLocation();

  const [notificationApi, contextHolder] = notification.useNotification();
  notification.config({ maxCount: 1 });

  const [isBtnActive, setIsBtnActive] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>('');
  const [isOpeningEntity, setOpeningEntity] = useState(false);
  const [list, setList] = useState<IEntity[]>([]);
  const [allList, setAllList] = useState<IEntity[]>([]);

  const [checkboxValues, setCheckboxValues] = useState<string[]>([]);
  const [indeterminate, setIndeterminate] = useState(false);
  const [checkAll, setCheckAll] = useState<boolean>(false);

  const { id } = params;
  const { marketingList }: { marketingList: ICrmMarketingList } =
    location.state;

  useEffect(() => {
    setIndeterminate(
      checkboxValues.length && checkboxValues.length !== list.length,
    );
    setCheckAll(checkboxValues.length === list.length);
  }, [checkboxValues]);

  const openEntity = async (id: string, type: EEntity) => {
    if (isOpeningEntity) {
      return;
    }
    setOpeningEntity(true);
    await Dynamics.openEntity(id, type);
    setOpeningEntity(false);
  };

  const checkMicrophonePermission = async (): Promise<PermissionState> => {
    const { state } = await navigator.permissions.query({
      name: 'microphone' as PermissionName,
    });
    if (state !== 'granted') {
      notificationApi.error({
        message: 'Microphone permission is required',
        placement: 'bottomRight',
        duration: 5,
        className: 'notifications',
      });
    }
    return state;
  };

  const makeCall = async (phone: string, user: IEntity) => {
    const state = await checkMicrophonePermission();
    if (state !== 'granted') return;

    if (SipJs.sessions?.length) {
      return;
    }

    const { isDialerActive } = State.state$.getValue();

    if (isDialerActive) {
      return;
    }

    Dynamics.searchEntityByCalls({ id: user.id }).then((entities) => {
      const updatedEntities: IEntity[] = [
        { ...entities[0], initialPhone: phone },
      ];
      commonMakeCall(updatedEntities, phone);
    });
  };

  const onChangeCheckbox = (values) => {
    setCheckboxValues(values as string[]);
  };

  const onCheckAllChange = (e) => {
    const isChecked = e.target.checked;

    setCheckboxValues(isChecked ? list.map((item) => item.id) : []);
    setCheckAll(isChecked);
  };

  const startCampaign = async () => {
    const state = await checkMicrophonePermission();
    if (state !== 'granted') return;

    setIsBtnActive(false);

    const dialerList = checkboxValues.map((id) => {
      return list.find((c) => c.id === id);
    });

    setActiveDialerList(dialerList);
    toggleDialler();

    setIsBtnActive(true);
  };

  const onSearchChange = (el) => {
    const { target } = el;

    setSearchValue(target.value);
  };

  const onSearch = (value: string) => {
    if (loading) {
      return;
    }

    setLoading(true);

    const clearValue = value.trim().toLowerCase();
    const filteredList = allList.filter((l) => {
      const name = l.fullName.toLowerCase();
      return name.includes(clearValue);
    });
    const chValues = filteredList.map((v) => v.id);

    setList(filteredList);
    setCheckboxValues(chValues);

    setLoading(false);
  };

  const onRefresh = async () => {
    setLoading(true);

    const filter = `listid eq ${id}`;
    const list = await Dynamics.getMarketingList(filter);
    const data = await Dynamics.getContactsForMarketingList(list[0]);
    const chValues = data.map((v) => v.id);

    setList(data);
    setCheckboxValues(chValues);
    setAllList(data);
    setSearchValue('');

    setLoading(false);
  };

  const getData = async () => {
    setLoading(true);

    const data = await Dynamics.getContactsForMarketingList(marketingList);
    const chValues = data.map((v) => v.id);

    setList(data);
    setCheckboxValues(chValues);
    setAllList(data);

    setLoading(false);
  };

  const showQuantity = (): string => {
    if (checkboxValues.length === 0) {
      return `No chosen ${marketingList.memberType}s`;
    }

    if (checkboxValues.length === 1) {
      return `${checkboxValues.length} ${marketingList.memberType}`;
    }

    return `${checkboxValues.length} ${marketingList.memberType}s`;
  };

  useEffect(() => {
    getData();
  }, []);

  return (
    <div
      className={`dialer ${isDialerActive ? 'dialer__active' : ''} ${page === CALL_PAGE_TYPE.MINIMIZED ? 'shortened' : ''}`}
    >
      {contextHolder}
      <div className="dialer__header">
        <Title level={5} ellipsis>
          List for Dialer: {`${marketingList.listName}`}
        </Title>
        <Tooltip title="Refresh">
          <Button
            onClick={onRefresh}
            type="dashed"
            shape="circle"
            icon={<RedoOutlined />}
          />
        </Tooltip>
      </div>
      <Search
        placeholder="Find and filter by name"
        allowClear
        value={searchValue}
        onSearch={onSearch}
        onChange={onSearchChange}
      />
      {loading ? (
        <Skeleton.Button size="small" />
      ) : (
        <Checkbox
          indeterminate={indeterminate}
          onChange={onCheckAllChange}
          checked={checkAll}
        >
          <Text>{showQuantity()}</Text>
        </Checkbox>
      )}

      <Checkbox.Group
        className="dialer__content"
        value={checkboxValues}
        onChange={onChangeCheckbox}
      >
        <List
          loading={loading}
          dataSource={list}
          renderItem={(item) => (
            <List.Item className="dialer__item" key={item.id}>
              <Checkbox value={item.id} />
              <EntityItem
                onMakeCall={makeCall}
                onOpenEntity={openEntity}
                user={item}
              ></EntityItem>
            </List.Item>
          )}
        />
      </Checkbox.Group>
      {!isDialerActive && (
        <Button
          type="primary"
          data-testid="start-campaign"
          onClick={startCampaign}
          disabled={!isBtnActive || !checkboxValues.length}
        >
          Start Campaign
        </Button>
      )}
    </div>
  );
});
